<script setup lang="ts">
import { computePosition, flip, offset, shift } from '@floating-ui/dom'
import type { Placement } from '@floating-ui/dom'
import { useDisplay } from 'vuetify'
import OnboardingStepCard from './OnboardingStepCard.vue'
import { getCurrentElements, useOnboardingStore } from '../stores/onboardingStore'
import { scrollIntoViewIfNeeded, unionRect } from '../utils/anchors'

/**
 * Единственный оверлей онбординга на всё приложение (монтируется в App.vue).
 * Затемнение — SVG-маска с вырезом по координатам подсвеченных элементов, поэтому элемент
 * виден через вырез на любом слое z-index. Координаты перечитываются каждый кадр,
 * пока идёт онбординг: так подсветка следует за прокруткой, ресайзом и анимациями.
 */
const onboardingStore = useOnboardingStore()
const { smAndDown } = useDisplay()

const HIGHLIGHT_PADDING = 6
const CARD_GAP = 14
const SCREEN_GAP = 16

const MOVE_DURATION_MS = 400

const highlight = ref<DOMRect | null>(null)
const cardRef = ref<HTMLElement | null>(null)
const cardStyle = ref<Record<string, string>>({})
const isCardReady = ref(false)

/*
 * Плавный переезд подсветки — только при смене шага. Между шагами координаты перечитываются
 * каждый кадр, и переход на каждом кадре давал бы запаздывание и рывки.
 */
const isMoving = ref(false)
let moveTimer = 0

let frameId = 0
let elements: HTMLElement[] = []

const cutout = computed(() => {
  // Во время перехода прежний элемент может уже размонтироваться — вырез не показываем, чтобы он не прыгал в угол
  const rect = onboardingStore.isTransitioning ? null : highlight.value
  if (!rect)
    return null

  return {
    x: rect.left - HIGHLIGHT_PADDING,
    y: rect.top - HIGHLIGHT_PADDING,
    width: rect.width + HIGHLIGHT_PADDING * 2,
    height: rect.height + HIGHLIGHT_PADDING * 2,
  }
})

const cutoutStyle = computed(() => {
  const rect = cutout.value
  if (!rect)
    return {}

  return {
    x: `${rect.x}px`,
    y: `${rect.y}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  }
})

const spotlightStyle = computed(() => {
  const rect = cutout.value
  if (!rect)
    return {}

  return {
    top: `${rect.y}px`,
    left: `${rect.x}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  }
})

const isSameRect = (a: DOMRect | null, b: DOMRect | null) =>
  a?.left === b?.left && a?.top === b?.top && a?.width === b?.width && a?.height === b?.height

async function placeCard() {
  const card = cardRef.value
  if (!card)
    return

  const rect = highlight.value

  if (!rect) {
    cardStyle.value = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    isCardReady.value = true

    return
  }

  // На узком экране карточка во всю ширину над или под элементом, как в мобильном приложении
  if (smAndDown.value) {
    const isTargetInUpperHalf = rect.top + rect.height / 2 < window.innerHeight / 2

    cardStyle.value = isTargetInUpperHalf
      ? { top: `${rect.bottom + HIGHLIGHT_PADDING + CARD_GAP}px`, left: `${SCREEN_GAP}px`, right: `${SCREEN_GAP}px` }
      : { bottom: `${window.innerHeight - rect.top + HIGHLIGHT_PADDING + CARD_GAP}px`, left: `${SCREEN_GAP}px`, right: `${SCREEN_GAP}px` }
    isCardReady.value = true

    return
  }

  const preferred = onboardingStore.currentStep?.placement
  const placement: Placement = preferred && preferred !== 'auto' ? preferred : 'bottom'

  /*
   * Элементу во всю высоту экрана карточка не помещается ни сверху, ни снизу — flip тогда уводит её наверх.
   * Если шаг явно задал сторону, оставляем её: ниже карточку прижмёт к краю окна, не закрывая заголовки таблицы.
   */
  const cardSpace = card.offsetHeight + HIGHLIGHT_PADDING + CARD_GAP + SCREEN_GAP
  const fitsVertically = rect.top >= cardSpace || window.innerHeight - rect.bottom >= cardSpace
  const keepSide = Boolean(preferred && preferred !== 'auto') && !fitsVertically

  const { x, y } = await computePosition(
    { getBoundingClientRect: () => rect },
    card,
    {
      strategy: 'fixed',
      placement,
      middleware: [
        offset(HIGHLIGHT_PADDING + CARD_GAP),
        ...(keepSide ? [] : [flip({ padding: SCREEN_GAP })]),
        shift({ padding: SCREEN_GAP }),
      ],
    },
  )

  /*
   * Элемент во весь экран (дерево, таблица) не оставляет места ни сверху, ни снизу — floating-ui
   * тогда выводит карточку за край. Прижимаем её к окну: пусть лучше перекроет часть элемента.
   */
  const maxTop = window.innerHeight - card.offsetHeight - SCREEN_GAP
  const maxLeft = window.innerWidth - card.offsetWidth - SCREEN_GAP
  const top = Math.max(SCREEN_GAP, Math.min(y, maxTop))
  const left = Math.max(SCREEN_GAP, Math.min(x, maxLeft))

  cardStyle.value = { top: `${top}px`, left: `${left}px` }
  isCardReady.value = true
}

function track() {
  const rect = unionRect(elements)

  // Элемент пропал (перерисовка, переход) — держим прежнюю позицию, пока движок не покажет следующий шаг
  if (rect && !isSameRect(rect, highlight.value)) {
    highlight.value = rect
    void placeCard()
  }

  frameId = window.requestAnimationFrame(track)
}

function stopTracking() {
  window.cancelAnimationFrame(frameId)
  window.clearTimeout(moveTimer)
  frameId = 0
}

watch(() => onboardingStore.stepToken, async () => {
  stopTracking()
  elements = getCurrentElements()
  await scrollIntoViewIfNeeded(elements)

  isMoving.value = true
  highlight.value = unionRect(elements)
  await nextTick()
  await placeCard()
  moveTimer = window.setTimeout(() => {
    isMoving.value = false
    frameId = window.requestAnimationFrame(track)
  }, MOVE_DURATION_MS)
})

watch(() => onboardingStore.isActive, isActive => {
  if (isActive)
    return

  stopTracking()
  elements = []
  highlight.value = null
  isCardReady.value = false
})

function onKeydown(event: KeyboardEvent) {
  if (!onboardingStore.isActive)
    return

  // stopPropagation: иначе Esc и стрелки дойдут до открытой под онбордингом формы (Esc закрывает её)
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    void onboardingStore.close()
  }
  else if (event.key === 'ArrowRight') {
    event.preventDefault()
    event.stopPropagation()
    void onboardingStore.next()
  }
  else if (event.key === 'ArrowLeft') {
    event.preventDefault()
    event.stopPropagation()
    void onboardingStore.prev()
  }
  else if (event.key === 'Tab' && cardRef.value && !cardRef.value.contains(document.activeElement)) {
    // Фокус не уходит со страницы под затемнением
    event.preventDefault()
    cardRef.value.querySelector<HTMLElement>('button')?.focus()
  }
}

useEventListener(window, 'keydown', onKeydown, { capture: true })
onBeforeUnmount(stopTracking)
</script>

<template>
  <Teleport to="body">
    <Transition name="onboarding-fade">
      <div
        v-if="onboardingStore.isActive && onboardingStore.currentStep"
        class="onboarding-overlay"
        @click.stop
        @wheel.prevent
        @touchmove.prevent
      >
        <svg
          class="onboarding-overlay__mask"
          aria-hidden="true"
        >
          <defs>
            <mask id="onboarding-spotlight-mask">
              <rect
                width="100%"
                height="100%"
                fill="white"
              />
              <rect
                v-if="cutout"
                class="onboarding-overlay__cutout"
                :class="{ 'onboarding-overlay--moving': isMoving }"
                :style="cutoutStyle"
                rx="10"
                ry="10"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            class="onboarding-overlay__backdrop"
            width="100%"
            height="100%"
            mask="url(#onboarding-spotlight-mask)"
          />
        </svg>

        <div
          v-if="cutout"
          class="onboarding-overlay__spotlight"
          :class="{ 'onboarding-overlay--moving': isMoving }"
          :style="spotlightStyle"
        />

        <div
          ref="cardRef"
          class="onboarding-overlay__card"
          :class="{ 'onboarding-overlay__card--ready': isCardReady }"
          :style="cardStyle"
        >
          <OnboardingStepCard
            :title="onboardingStore.currentText?.title ?? ''"
            :text="onboardingStore.currentText?.text ?? ''"
            :position="onboardingStore.position"
            :total="onboardingStore.totalSteps"
            :is-loading="onboardingStore.isTransitioning"
            @prev="onboardingStore.prev()"
            @next="onboardingStore.next()"
            @close="onboardingStore.close()"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.onboarding-overlay {
  position: fixed;
  inset: 0;
  pointer-events: auto;
}

.onboarding-overlay__mask {
  position: absolute;
  block-size: 100%;
  inline-size: 100%;
  inset: 0;
  pointer-events: none;
}

.onboarding-overlay__backdrop {
  fill: rgb(var(--v-theme-onboarding-backdrop));
  fill-opacity: var(--v-onboarding-backdrop-opacity);
}

.onboarding-overlay__cutout.onboarding-overlay--moving {
  transition: x 0.4s cubic-bezier(0.4, 0, 0.2, 1), y 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.onboarding-overlay__spotlight.onboarding-overlay--moving {
  transition: top 0.4s cubic-bezier(0.4, 0, 0.2, 1), left 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.onboarding-overlay__spotlight {
  position: fixed;
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 10px;
  pointer-events: none;

  &::after {
    position: absolute;
    border: 2px solid rgb(var(--v-theme-primary));
    border-radius: 12px;
    animation: onboarding-pulse 2s ease-in-out infinite;
    content: "";
    inset: -4px;
  }
}

.onboarding-overlay__card {
  position: fixed;
  max-block-size: calc(100vh - 32px);
  inline-size: 380px;
  max-inline-size: calc(100vw - 32px);
  opacity: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.onboarding-overlay__card--ready {
  opacity: 1;
}

@media (max-width: 959px) {
  .onboarding-overlay__card {
    inline-size: auto;
  }
}

/* Расширение на фиксированные пиксели, а не scale: у больших элементов (таблица, дерево) масштаб уводил рамку далеко за элемент */
@keyframes onboarding-pulse {
  0%,
  100% {
    inset: -4px;
    opacity: 0;
  }

  50% {
    inset: -10px;
    opacity: 0.6;
  }
}

.onboarding-fade-enter-active {
  transition: opacity 0.5s ease;
}

.onboarding-fade-leave-active {
  transition: opacity 0.3s ease;
}

.onboarding-fade-enter-from,
.onboarding-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .onboarding-overlay__cutout,
  .onboarding-overlay__spotlight,
  .onboarding-overlay__card {
    transition: none;
  }

  .onboarding-overlay__spotlight::after {
    animation: none;
  }
}
</style>
